/**
 * Hook: useCallTracking
 * Maneja el estado de llamadas con persistencia en BD y timer en tiempo real
 *
 * Flujo de llamada:
 * 1. startCall() → Llamada activa (badge verde)
 * 2. endCall() → Pendiente de nota (badge ámbar) - NO llama API aún
 * 3. confirmCallWithNote() o dismissCall() → Guarda en BD y desaparece
 */

import { useEffect, useCallback, useState, useRef } from 'react'
import useCRMStore from '../store/useCRMStore'
import { callsAPI } from '../services/api'

export const useCallTracking = () => {
  const {
    activeCalls,
    pendingNoteCalls,
    callsLoaded,
    setActiveCalls,
    addActiveCall,
    removeActiveCall,
    moveCallToPendingNote,
    removePendingNoteCall,
    getPendingNoteCall,
    isInCall,
    getCallForContact,
    user
  } = useCRMStore()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const recoveryAttempted = useRef(false)

  // Recovery: Cargar llamadas activas desde BD al montar
  useEffect(() => {
    if (!recoveryAttempted.current && user && !callsLoaded) {
      recoveryAttempted.current = true
      recoverActiveCalls()
    }
  }, [user, callsLoaded])

  const recoverActiveCalls = async () => {
    try {
      setLoading(true)
      const response = await callsAPI.getActiveCalls()
      setActiveCalls(response.calls || [])
      console.log('📞 Llamadas recuperadas:', response.calls?.length || 0)
    } catch (err) {
      console.error('Error recovering active calls:', err)
      setError(err.message)
      // Si hay error, marcar como cargado para no reintentar infinitamente
      setActiveCalls([])
    } finally {
      setLoading(false)
    }
  }

  const startCall = useCallback(async (contact) => {
    if (!contact?.id) {
      return { success: false, error: 'Contacto inválido' }
    }

    if (isInCall(contact.id)) {
      return { success: false, error: 'Ya hay una llamada activa con este contacto' }
    }

    try {
      setLoading(true)
      setError(null)
      const response = await callsAPI.startCall(contact.id)

      addActiveCall({
        id: response.call.id,
        contactoId: contact.id,
        contactPhone: contact.phone,
        contactName: contact.name || contact.phone,
        startedAt: response.call.started_at,
        agentPhone: user?.phone,
        agentName: user?.name
      })

      console.log('📞 Llamada iniciada:', contact.name || contact.phone)
      return { success: true, callId: response.call.id }
    } catch (err) {
      console.error('Error starting call:', err)
      const errorMsg = err.response?.data?.error || 'Error iniciando llamada'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [addActiveCall, isInCall, user])

  /**
   * Termina una llamada y la mueve a "pendiente de nota"
   * NO llama a la API aún - espera confirmación con nota o descarte
   */
  const endCall = useCallback((callId) => {
    const pendingCall = moveCallToPendingNote(callId)
    if (pendingCall) {
      console.log('📞 Llamada terminada, esperando nota:', pendingCall.contactName)
      return { success: true, pendingCall }
    }
    return { success: false, error: 'Llamada no encontrada' }
  }, [moveCallToPendingNote])

  /**
   * Confirma el fin de la llamada CON nota y guarda en BD
   */
  const confirmCallWithNote = useCallback(async (callId, note) => {
    try {
      setLoading(true)
      setError(null)

      const response = await callsAPI.endCall(callId, note || null)
      removePendingNoteCall(callId)

      console.log('📞 Llamada guardada con nota, duración:', response.call.duration_seconds, 's')
      return {
        success: true,
        duration: response.call.duration_seconds
      }
    } catch (err) {
      console.error('Error confirmando llamada:', err)
      const errorMsg = err.response?.data?.error || 'Error guardando llamada'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [removePendingNoteCall])

  /**
   * Descarta la llamada SIN nota (cierra sin guardar nota)
   */
  const dismissCall = useCallback(async (callId) => {
    try {
      setLoading(true)
      setError(null)

      const response = await callsAPI.endCall(callId, null)
      removePendingNoteCall(callId)

      console.log('📞 Llamada cerrada sin nota, duración:', response.call.duration_seconds, 's')
      return {
        success: true,
        duration: response.call.duration_seconds
      }
    } catch (err) {
      console.error('Error cerrando llamada:', err)
      const errorMsg = err.response?.data?.error || 'Error cerrando llamada'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setLoading(false)
    }
  }, [removePendingNoteCall])

  // Formatear duración desde timestamp ISO a "MM:SS" o "HH:MM:SS"
  const getCallDuration = useCallback((startedAt) => {
    if (!startedAt) return '0:00'

    const start = new Date(startedAt)
    const now = new Date()
    const seconds = Math.max(0, Math.floor((now - start) / 1000))

    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }, [])

  // Formatear segundos a "MM:SS" o "HH:MM:SS"
  const formatDuration = useCallback((totalSeconds) => {
    if (!totalSeconds) return '0:00'

    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const secs = totalSeconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }, [])

  return {
    activeCalls,
    pendingNoteCalls,
    loading,
    error,
    startCall,
    endCall,
    confirmCallWithNote,
    dismissCall,
    isInCall,
    getCallForContact,
    getPendingNoteCall,
    getCallDuration,
    formatDuration,
    recoverActiveCalls
  }
}
